// src/screens/EnviosScreen.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import {
  IoArrowBack,
  IoPencil,
  IoQrCodeOutline,
  IoAdd,
  // eslint-disable-next-line no-unused-vars
  IoTimeOutline,
  IoCubeOutline
} from 'react-icons/io5';
import {
  FaAmazon,
  FaUber,
  FaDhl,
  FaUps,
  FaFedex
} from 'react-icons/fa';
import { SiMercadopago } from 'react-icons/si'; // MercadoLibre approx
import AccesoModal from '../components/AccesoModal';
import './EnviosScreen.css'; // --- Premium CSS ---
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';

// Helper for Brand Colors & Icons
const getBrandConfig = (empresaOriginal) => {
  const empresa = empresaOriginal.toLowerCase();

  if (empresa.includes('amazon')) return {
    color: '#FF9900',
    bg: '#FFF3E0',
    icon: <FaAmazon />
  };
  if (empresa.includes('mercadolibre') || empresa.includes('mercado libre')) return {
    color: '#ffe600',
    bg: '#fffdeb',
    icon: <SiMercadopago /> // Using MP logo as proxy or generic box
  };
  if (empresa.includes('uber')) return {
    color: '#000000',
    bg: '#e5e7eb',
    icon: <FaUber />
  };
  if (empresa.includes('dhl')) return {
    color: '#d40511',
    bg: '#fee2e2',
    icon: <FaDhl />
  };
  if (empresa.includes('ups')) return {
    color: '#ffb500',
    bg: '#fff7ed',
    icon: <FaUps />
  };
  if (empresa.includes('fedex')) return {
    color: '#4d148c',
    bg: '#f3e8ff',
    icon: <FaFedex />
  };

  // Default
  return {
    color: '#4b5563',
    bg: '#f3f4f6',
    icon: <IoCubeOutline />
  };
};

function EnviosScreen() {
  const navigate = useNavigate();
  const [envios, setEnvios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAcceso, setSelectedAcceso] = useState(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, "accesos"),
      where("residenteId", "==", currentUser.uid)
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const listaEnvios = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const isEnvio = data.tipo === 'Envio' || data.tipo?.toLowerCase().includes('paqueter') || !!data.empresa;
        // Excluir registros puramente de bitácora histórica de entregas individuales para mostrar el pase principal
        if (isEnvio && !data.accesoOriginalId) {
          listaEnvios.push({ ...data, id: doc.id });
        }
      });

      listaEnvios.sort((a, b) => {
        const dateA = a.fechaCreacion?.toDate ? a.fechaCreacion.toDate() : (a.fecha?.toDate ? a.fecha.toDate() : new Date(0));
        const dateB = b.fechaCreacion?.toDate ? b.fechaCreacion.toDate() : (b.fecha?.toDate ? b.fecha.toDate() : new Date(0));
        return dateB - dateA;
      });

      setEnvios(listaEnvios);
      setLoading(false);
    }, (error) => {
      console.error("Error al cargar envíos: ", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser]);

  const handleVerAcceso = (acceso) => {
    setSelectedAcceso(acceso);
  };

  return (
    <>
      <KondoBrandHeader showBack={false} />
      <div className="envios-container">
        {/* --- Clean Navbar (Back Left, Action Right) --- */}
        {/* Sticky Compact Header */}
        <div className="envios-fixed-wrapper">
          <div className="envios-banner-compact">
            <button onClick={() => navigate('/accesos')} className="envios-back-btn glass">
              <IoArrowBack size={24} />
            </button>

            <div className="envios-banner-content">
              <h2>Gestión de Paquetería</h2>
              <p>Tus envíos y entregas</p>
            </div>

            <button className="envios-action-btn glass" onClick={() => navigate('/accesos/envios/nuevo')}>
              <IoAdd size={24} />
            </button>

            <div className="envios-banner-icon">
              <IoCubeOutline />
            </div>
          </div>
        </div>

        <div className="envios-grid-container">
          {loading && <p style={{ textAlign: 'center', color: '#6b7280' }}>Cargando envíos...</p>}

          {!loading && envios.length === 0 && (
            <div className="empty-state">
              <IoCubeOutline size={48} style={{ opacity: 0.3, marginBottom: 10 }} />
              <p>No tienes envíos programados.</p>
              <p style={{ fontSize: '0.9rem', marginTop: 5 }}>Registra uno para dar acceso a paquetería.</p>
            </div>
          )}

          {/* --- Grid --- */}
          {!loading && envios.length > 0 && (
            <div className="envios-grid">
              {envios.map((envio) => {
                const brand = getBrandConfig(envio.empresa);
                const isExpired = envio.expira?.toDate ? envio.expira.toDate() < new Date() : false;
                const isExhausted = !envio.esIlimitado && envio.usosMaximos > 0 && ((envio.usosActuales || 0) >= envio.usosMaximos);
                const isActivo = envio.activo && !isExpired && !isExhausted && envio.estatus !== 'Completado';

                return (
                  <div key={envio.id} className="envio-card">
                    {/* Thin Colored Strip */}
                    <div className="envio-card-header" style={{ backgroundColor: brand.color }}></div>

                    <div className="envio-card-body">
                      <div className="envio-top-row">
                        <div className="envio-icon-wrapper" style={{ color: brand.color, backgroundColor: brand.bg }}>
                          {brand.icon}
                        </div>

                        <div className="envio-info-col">
                          <h3 className="envio-brand-name">{envio.empresa}</h3>
                          <span className={`envio-status-badge ${isActivo ? 'active' : 'inactive'}`}>
                            {isActivo ? 'Activo' : (isExhausted ? 'Agotado' : (isExpired ? 'Exp.' : 'Inactivo'))}
                          </span>
                        </div>

                        <button
                          className="envio-edit-btn"
                          onClick={() => navigate(`/accesos/envios/editar/${envio.id}`)}
                          title="Editar vigencia o habilitación"
                        >
                          <IoPencil size={18} />
                        </button>
                      </div>

                      <div className="envio-info">
                        <span>
                          {envio.esIlimitado || envio.usosMaximos === -1 
                            ? `Ilimitado (${envio.usosActuales || 0} entregas)` 
                            : `Usos: ${envio.usosActuales || 0}/${envio.usosMaximos || 1}`}
                        </span>
                        <span style={{ opacity: 0.8 }}>Vence: {envio.expira?.toDate ? envio.expira.toDate().toLocaleDateString() : 'N/A'}</span>
                      </div>

                      <button className="btn-view-qr" onClick={() => handleVerAcceso(envio)}>
                        <IoQrCodeOutline size={16} />
                        QR / PIN
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {selectedAcceso && (
          <AccesoModal
            acceso={selectedAcceso}
            onClose={() => setSelectedAcceso(null)}
          />
        )}
      </div>
      <KondoBottomNav activeRoute="/accesos" />
    </>
  );
}

export default EnviosScreen;